import React, { useMemo, useState } from 'react';
import { Col, Row, Tabs, Tag } from 'antd';
import { useI18n } from 'hooks/useI18n';
import TableComponent from 'components/table-component';
import { SMSPageShell, SMSPanel } from '../shared';

const STATUS_COLORS = {
	sent: 'green',
	queued: 'blue',
	failed: 'red',
};

const SMSHistory = () => {
	const { t } = useI18n();
	const [activeTab, setActiveTab] = useState('bulk');

	const bulkHistory = useMemo(
		() => [
			{ key: '1', campaign: t('sms.history.bulk.campaign_renewal'), audience: t('sms.bulk.options.active_member'), sentAt: '18 May 2026, 09:15 AM', delivered: 72, failed: 5, cost: 'Rs. 360', status: 'sent' },
			{ key: '2', campaign: t('sms.history.bulk.campaign_offer'), audience: t('sms.bulk.options.pending_payment'), sentAt: '16 May 2026, 07:40 PM', delivered: 41, failed: 7, cost: 'Rs. 240', status: 'queued' },
			{ key: '3', campaign: t('sms.history.bulk.campaign_reactivation'), audience: t('sms.bulk.options.inactive_member'), sentAt: '14 May 2026, 08:05 AM', delivered: 58, failed: 6, cost: 'Rs. 320', status: 'failed' },
		],
		[t]
	);

	const singleHistory = useMemo(
		() => [
			{ key: '1', recipient: 'Arun Kumar', phone: '98765 43210', sentAt: '18 May 2026, 11:10 AM', delivered: 1, failed: 0, cost: 'Rs. 5', status: 'sent' },
			{ key: '2', recipient: 'Priya S', phone: '91234 56789', sentAt: '17 May 2026, 05:20 PM', delivered: 1, failed: 0, cost: 'Rs. 5', status: 'sent' },
			{ key: '3', recipient: 'Karthik R', phone: '90000 12345', sentAt: '15 May 2026, 01:45 PM', delivered: 0, failed: 1, cost: 'Rs. 5', status: 'failed' },
		],
		[]
	);

	const historyColumns = useMemo(
		() => ({
			bulk: [
				{ title: t('sms.history.columns.campaign'), dataIndex: 'campaign', key: 'campaign' },
				{ title: t('sms.history.columns.audience'), dataIndex: 'audience', key: 'audience', render: (value) => <span className="sms-table-tag">{value}</span> },
				{ title: t('sms.history.columns.sent_at'), dataIndex: 'sentAt', key: 'sentAt' },
				{ title: t('sms.history.columns.delivered'), dataIndex: 'delivered', key: 'delivered' },
				{ title: t('sms.history.columns.failed'), dataIndex: 'failed', key: 'failed' },
				{ title: t('sms.history.columns.cost'), dataIndex: 'cost', key: 'cost' },
				{ title: t('sms.history.columns.status'), dataIndex: 'status', key: 'status', render: (value) => <Tag className="sms-status-tag" color={STATUS_COLORS[value]}>{t(`sms.history.status.${value}`)}</Tag> },
			],
			single: [
				{ title: t('sms.history.columns.recipient'), dataIndex: 'recipient', key: 'recipient' },
				{ title: t('sms.history.columns.phone'), dataIndex: 'phone', key: 'phone' },
				{ title: t('sms.history.columns.sent_at'), dataIndex: 'sentAt', key: 'sentAt' },
				{ title: t('sms.history.columns.delivered'), dataIndex: 'delivered', key: 'delivered' },
				{ title: t('sms.history.columns.failed'), dataIndex: 'failed', key: 'failed' },
				{ title: t('sms.history.columns.cost'), dataIndex: 'cost', key: 'cost' },
				{ title: t('sms.history.columns.status'), dataIndex: 'status', key: 'status', render: (value) => <Tag className="sms-status-tag" color={STATUS_COLORS[value]}>{t(`sms.history.status.${value}`)}</Tag> },
			],
		}),
		[t]
	);

	const activeRows = activeTab === 'bulk' ? bulkHistory : singleHistory;
	const totalDelivered = activeRows.reduce((sum, row) => sum + row.delivered, 0);
	const totalFailed = activeRows.reduce((sum, row) => sum + row.failed, 0);

	return (
		<SMSPageShell
			eyebrow={t('navigation.sms')}
			title={t('sms.history.title')}
			subtitle={t('sms.history.subtitle')}
			creditLabel={t('sms.common.available_credits')}
			meta={[
				{ label: t('sms.history.metrics.campaigns'), value: activeRows.length },
				{ label: t('sms.history.metrics.delivered'), value: totalDelivered },
				{ label: t('sms.history.metrics.failed'), value: totalFailed },
			]}
		>
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<SMSPanel title={t('sms.history.tabs.title')} subtitle={t('sms.history.tabs.subtitle')}>
						<div className="sms-history-summary-grid">
							<div className="sms-history-summary">
								<span className="sms-history-summary__label">{t('sms.history.metrics.latest_channel')}</span>
								<strong className="sms-history-summary__value">{activeTab === 'bulk' ? t('sms.history.tabs.bulk') : t('sms.history.tabs.single')}</strong>
							</div>
							<div className="sms-history-summary">
								<span className="sms-history-summary__label">{t('sms.history.metrics.delivery_rate')}</span>
								<strong className="sms-history-summary__value">{Math.max(totalDelivered + totalFailed, 1) ? `${Math.round((totalDelivered / Math.max(totalDelivered + totalFailed, 1)) * 100)}%` : '0%'}</strong>
							</div>
							<div className="sms-history-summary">
								<span className="sms-history-summary__label">{t('sms.history.metrics.last_sync')}</span>
								<strong className="sms-history-summary__value">18 May 2026</strong>
							</div>
						</div>
						<Tabs
							className="sms-tabs"
							activeKey={activeTab}
							onChange={setActiveTab}
							items={[
								{
									key: 'bulk',
									label: t('sms.history.tabs.bulk'),
									children: <TableComponent rowKey="key" columns={historyColumns.bulk} dataSource={bulkHistory} pagination={false} />,
								},
								{
									key: 'single',
									label: t('sms.history.tabs.single'),
									children: <TableComponent rowKey="key" columns={historyColumns.single} dataSource={singleHistory} pagination={false} />,
								},
							]}
						/>
					</SMSPanel>
				</Col>
			</Row>
		</SMSPageShell>
	);
};

export default SMSHistory;
